import { useEffect, useRef } from "react";
import "./ButterflyEffect.css";

// 8 butterflies (trimmed from 15) — halves RAF compute cost with no visible loss.
// Chosen for maximum colour diversity across the visible spectrum.
const BUTTERFLIES = [
  // Blue Morpho
  { lerpT: 0.055, flapDur: "0.26s", size: 22, phase: 0.00, driftFqX: 0.50, driftFqY: 0.40, spread: 32, c1: "#1565C0", c2: "#42A5F5" },
  // Monarch
  { lerpT: 0.038, flapDur: "0.24s", size: 19, phase: 0.84, driftFqX: 0.70, driftFqY: 0.55, spread: 24, c1: "#E65100", c2: "#FFA726" },
  // Yellow Swallowtail
  { lerpT: 0.062, flapDur: "0.30s", size: 21, phase: 1.68, driftFqX: 0.40, driftFqY: 0.65, spread: 28, c1: "#F9A825", c2: "#FFF176" },
  // Purple Emperor
  { lerpT: 0.030, flapDur: "0.32s", size: 16, phase: 2.51, driftFqX: 0.60, driftFqY: 0.35, spread: 20, c1: "#6A1B9A", c2: "#CE93D8" },
  // Malachite
  { lerpT: 0.050, flapDur: "0.26s", size: 24, phase: 3.35, driftFqX: 0.35, driftFqY: 0.70, spread: 36, c1: "#1B5E20", c2: "#66BB6A" },
  // Ulysses Blue
  { lerpT: 0.072, flapDur: "0.22s", size: 20, phase: 5.03, driftFqX: 0.55, driftFqY: 0.65, spread: 30, c1: "#006064", c2: "#00BCD4" },
  // Rose (theme accent)
  { lerpT: 0.048, flapDur: "0.27s", size: 18, phase: 2.93, driftFqX: 0.75, driftFqY: 0.60, spread: 22, c1: "#E91E8C", c2: "#FF80AB" },
  // Emerald Swallowtail
  { lerpT: 0.035, flapDur: "0.38s", size: 17, phase: 0.42, driftFqX: 0.62, driftFqY: 0.38, spread: 23, c1: "#004D40", c2: "#80CBC4" },
];

const lerp = (a, b, t) => a + (b - a) * t;

/* ─── Butterfly SVG ───────────────────────────────────────────────────────
   ViewBox 34×32.  Body at x=17.
   Upper wings: broad spread with rounded apex.
   Lower wings: fan shape with a small swallowtail tail on outer edge.
   Wing veins, spots, outline stroke for illustration depth.
─────────────────────────────────────────────────────────────────────────── */
const ButterflyIcon = () => (
  <svg viewBox="0 0 34 32" xmlns="http://www.w3.org/2000/svg" overflow="visible">

    {/* Antennae */}
    <path d="M16 7 Q12 3 9 0.5"   className="bf-antenna" />
    <circle cx="9"  cy="0.5" r="1.3" className="bf-anttip" />
    <path d="M18 7 Q22 3 25 0.5"  className="bf-antenna" />
    <circle cx="25" cy="0.5" r="1.3" className="bf-anttip" />

    {/* LEFT wings */}
    <g className="bf-lw">
      {/* upper-left — broad sweep */}
      <path d="M17 10 C14 4, 5 1, 2 7 C-1 12, 3 17, 17 17" className="bf-up" />
      {/* lower-left — fan with swallowtail tip */}
      <path d="M17 16 C12 16, 3 19, 2 25 C1 29, 5 31, 9 29 C11 28, 10 26, 17 23" className="bf-lo" />
      {/* spots */}
      <circle cx="6"  cy="8"  r="1.8" className="bf-spot" />
      <circle cx="4"  cy="13" r="1.2" className="bf-spot" />
      <circle cx="6"  cy="26" r="1.4" className="bf-spot" />
      <circle cx="10" cy="20" r="0.9" className="bf-spot" />
      {/* veins */}
      <path d="M17 13 L5 9"   className="bf-vein" />
      <path d="M17 16 L5 15"  className="bf-vein" />
      <path d="M17 18 L8 24"  className="bf-vein" />
    </g>

    {/* RIGHT wings (mirror) */}
    <g className="bf-rw">
      {/* upper-right */}
      <path d="M17 10 C20 4, 29 1, 32 7 C35 12, 31 17, 17 17" className="bf-up" />
      {/* lower-right */}
      <path d="M17 16 C22 16, 31 19, 32 25 C33 29, 29 31, 25 29 C23 28, 24 26, 17 23" className="bf-lo" />
      {/* spots */}
      <circle cx="28" cy="8"  r="1.8" className="bf-spot" />
      <circle cx="30" cy="13" r="1.2" className="bf-spot" />
      <circle cx="28" cy="26" r="1.4" className="bf-spot" />
      <circle cx="24" cy="20" r="0.9" className="bf-spot" />
      {/* veins */}
      <path d="M17 13 L29 9"   className="bf-vein" />
      <path d="M17 16 L29 15"  className="bf-vein" />
      <path d="M17 18 L26 24"  className="bf-vein" />
    </g>

    {/* Body */}
    <ellipse cx="17" cy="11" rx="2.2" ry="3.5" className="bf-thorax"  />
    <ellipse cx="17" cy="20" rx="1.6" ry="6.5" className="bf-abdomen" />
    <circle  cx="17" cy="7"  r="2.4"            className="bf-head"    />
  </svg>
);

const ButterflyEffect = () => {
  const refs = useRef(BUTTERFLIES.map(() => null));

  useEffect(() => {
    // Skip on touch devices and when user prefers reduced motion (accessibility)
    if (window.matchMedia("(hover: none) and (pointer: coarse)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const els = refs.current;

    const state = BUTTERFLIES.map((cfg) => ({
      x: -500, y: -500,
      px: -500, py: -500,
      vx: 0, vy: 0,
      rot: 0,
      mode: "follow",
      lerpT:    cfg.lerpT,
      phase:    cfg.phase,
      driftFqX: cfg.driftFqX,
      driftFqY: cfg.driftFqY,
      spread:   cfg.spread,
    }));

    let mx = -500, my = -500;
    let cvx = 0, cvy = 0;   // cursor velocity (decays each frame)
    let time = 0;
    let raf;
    let visible = false;

    const show = () => {
      if (visible) return;
      visible = true;
      els.forEach(el => { if (el) el.style.opacity = "1"; });
    };
    const hide = () => {
      visible = false;
      els.forEach(el => { if (el) el.style.opacity = "0"; });
    };

    const onMove = (e) => {
      cvx = e.clientX - mx;
      cvy = e.clientY - my;
      mx = e.clientX;
      my = e.clientY;
      show();
    };

    /* Scrolling fires no mousemove — inject velocity so idleFactor
       stays low and butterflies keep chasing instead of orbiting. */
    const onScroll = () => { cvx = 8; cvy = 8; };

    /* Staggered scatter — butterflies startle at slightly different times */
    const onClick = () => {
      state.forEach((b, i) => {
        setTimeout(() => {
          const angle = Math.random() * Math.PI * 2;
          const speed = 13 + Math.random() * 18;
          b.vx = Math.cos(angle) * speed;
          b.vy = Math.sin(angle) * speed;
          b.mode = "scatter";
          const el = els[i];
          if (el) {
            el.classList.add("bf-burst");
            setTimeout(() => el?.classList.remove("bf-burst"), 550);
          }
        }, Math.random() * 130);
      });
    };

    const tick = () => {
      time += 0.016;

      /* Decay cursor velocity — idleFactor → 1 when cursor is still */
      cvx *= 0.78;
      cvy *= 0.78;
      const idleFactor = Math.max(0, 1 - Math.hypot(cvx, cvy) / 6);

      state.forEach((b, i) => {
        b.px = b.x;
        b.py = b.y;

        if (b.mode === "follow") {
          /* ── Moving: Lissajous drift behind cursor ── */
          const driftX = Math.sin(time * b.driftFqX + b.phase)       * b.spread;
          const driftY = Math.cos(time * b.driftFqY + b.phase * 1.4) * b.spread;

          /* ── Idle: clear circular orbit around cursor ── */
          const orbitSpeed = 0.45 + b.driftFqX * 0.45;
          const orbitR     = 14  + b.spread * 0.48;
          const orbitAng   = time * orbitSpeed + b.phase;
          const orbitX     = Math.cos(orbitAng) * orbitR;
          const orbitY     = Math.sin(orbitAng) * orbitR;

          /* Blend: idleFactor=0 → follow drift, idleFactor=1 → orbit */
          const tx = mx + driftX + (orbitX - driftX) * idleFactor;
          const ty = my + driftY + (orbitY - driftY) * idleFactor;

          /* Speed up lerp slightly when idle so orbit is clearly visible */
          const t = b.lerpT + idleFactor * 0.045;
          b.x = lerp(b.x, tx, t);
          b.y = lerp(b.y, ty, t);

        } else if (b.mode === "scatter") {
          b.vx *= 0.90;
          b.vy *= 0.90;
          b.x += b.vx;
          b.y += b.vy;
          if (Math.abs(b.vx) < 0.55 && Math.abs(b.vy) < 0.55) b.mode = "return";

        } else { /* return */
          const rdx = Math.sin(time * b.driftFqX + b.phase)       * b.spread;
          const rdy = Math.cos(time * b.driftFqY + b.phase * 1.4) * b.spread;
          b.x = lerp(b.x, mx + rdx, 0.018);
          b.y = lerp(b.y, my + rdy, 0.018);
          if (Math.hypot(b.x - (mx + rdx), b.y - (my + rdy)) < 8) b.mode = "follow";
        }

        /* Rotate to face direction of travel */
        const dx = b.x - b.px;
        const dy = b.y - b.py;
        if (dx * dx + dy * dy > 0.05) {
          b.rot = lerp(b.rot, Math.atan2(dy, dx) * (180 / Math.PI) + 90, 0.10);
        }

        const el = els[i];
        if (el) {
          el.style.transform =
            `translate(${b.x}px,${b.y}px) translate(-50%,-50%) rotate(${b.rot}deg)`;
        }
      });

      raf = requestAnimationFrame(tick);
    };

    document.addEventListener("mousemove",  onMove,  { passive: true });
    document.addEventListener("mouseleave", hide);
    document.addEventListener("click",      onClick);
    window.addEventListener("scroll",       onScroll, { passive: true });
    tick();

    return () => {
      document.removeEventListener("mousemove",  onMove);
      document.removeEventListener("mouseleave", hide);
      document.removeEventListener("click",      onClick);
      window.removeEventListener("scroll",       onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <>
      {BUTTERFLIES.map((cfg, i) => (
        <div
          key={i}
          ref={el => (refs.current[i] = el)}
          className="bf"
          style={{
            "--flap": cfg.flapDur,
            "--c1":   cfg.c1,
            "--c2":   cfg.c2,
            width:  `${cfg.size}px`,
            height: `${cfg.size}px`,
          }}
          aria-hidden="true"
        >
          <ButterflyIcon />
        </div>
      ))}
    </>
  );
};

export default ButterflyEffect;
